  import React, { useEffect, useState, useCallback } from 'react';
import { collection, getDocs, query, where, orderBy, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useLocation } from 'react-router-dom'; // Import useLocation
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import './MonitoringAlat.css'; // Keep existing CSS for container styling

// Setup the localizer by providing the moment Object
const localizer = momentLocalizer(moment);

// Helper to convert Firestore Timestamp to JS Date
const timestampToDate = (timestamp) => {
  return timestamp instanceof Timestamp ? timestamp.toDate() : null;
};

const MonitoringAlat = () => {
  const [alatList, setAlatList] = useState([]); // Combined list of instruments and glassware
  const [selectedAlat, setSelectedAlat] = useState(null); // Store the selected alat object
  const [scheduleEvents, setScheduleEvents] = useState([]);
  const [loadingAlat, setLoadingAlat] = useState(false);
  const [loadingSchedule, setLoadingSchedule] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation(); // Get location object

  // Fetch list of instruments and glassware
  const fetchAlatList = useCallback(async () => { // Wrap in useCallback
    setLoadingAlat(true);
    setError('');
    try {
      const instrumenRef = collection(db, 'instrumen');
      const alatGelasRef = collection(db, 'alat_gelas');

      const [instrumenSnapshot, alatGelasSnapshot] = await Promise.all([
        getDocs(instrumenRef),
        getDocs(alatGelasRef)
      ]);

      const instrumenData = instrumenSnapshot.docs.map(doc => ({ id: doc.id, type: 'instrumen', ...doc.data() }));
      const alatGelasData = alatGelasSnapshot.docs.map(doc => ({ id: doc.id, type: 'alat_gelas', ...doc.data() }));

      // Combine and sort
      const combinedList = [...instrumenData, ...alatGelasData].sort((a, b) => a.nama.localeCompare(b.nama));
      setAlatList(combinedList);

      // --- Auto-select based on location state ---
      if (location.state?.alatId && combinedList.length > 0) {
        const preselectedAlat = combinedList.find(item => item.id === location.state.alatId);
        if (preselectedAlat) {
          // Use timeout to ensure state updates are finished
          setTimeout(() => fetchScheduleForAlat(preselectedAlat), 0);
        } else {
          console.warn(`Preselected alat with ID ${location.state.alatId} not found in the fetched list.`);
        }
      }
      // --- End auto-select ---

    } catch (err) {
      console.error("Error fetching tools list:", err);
      setError('Gagal memuat daftar alat.');
    } finally {
      setLoadingAlat(false);
    }
  }, [location.state]); // Add location.state dependency

  // Fetch approved requests for the selected alat
  const fetchScheduleForAlat = useCallback(async (alat) => {
    if (!alat) return;
    setLoadingSchedule(true);
    setError('');
    setSelectedAlat(alat); // Set the selected alat
    setScheduleEvents([]); // Clear previous schedule
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      // Query for approved requests for the specific alat name
      const q = query(
        requestsRef,
        where('status', '==', 'approved'),
        where('namaAlat', '==', alat.nama), // Filter by selected alat name
        orderBy('tanggalPinjam', 'asc') // Order by borrow date
      );

      const querySnapshot = await getDocs(q);
      const events = querySnapshot.docs.map(docSnap => {
        const data = docSnap.data();
        const docId = docSnap.id;
        try {
          const start = timestampToDate(data.tanggalPinjam);
          const end = timestampToDate(data.tanggalKembali);

          if (!start || !end) {
            console.warn(`Invalid or missing date found for request ${docId} (${alat.nama}). Start: ${data.tanggalPinjam}, End: ${data.tanggalKembali}`);
            return null;
          }

          let adjustedEnd = new Date(end.getTime());
          if (moment(start).isSame(adjustedEnd, 'day')) {
              adjustedEnd.setHours(adjustedEnd.getHours() + 1);
          }

          return {
            id: docId,
            title: `${data.namaUser || 'N/A'} (${data.jumlah || 1})`, // Show user and quantity
            start: start,
            end: adjustedEnd,
            allDay: false,
            resource: data,
          };
        } catch (mapError) {
          console.error(`Error processing booking document ${docId} for ${alat.nama}:`, mapError, "Data:", data);
          setError(`Terjadi kesalahan saat memproses sebagian data jadwal untuk ${alat.nama}.`);
          return null;
        }
      }).filter(event => event !== null);

      setScheduleEvents(events);
    } catch (err) {
      console.error(`Error fetching schedule for ${alat.nama}:`, err);
      setError(`Gagal memuat jadwal untuk ${alat.nama}.`);
    } finally {
      setLoadingSchedule(false);
    }
  }, []);

  // Fetch alat list on component mount and handle pre-selection
  useEffect(() => {
    fetchAlatList();
  }, [fetchAlatList]); // Add fetchAlatList as dependency

  return (
    // Use existing container class or create a new one if layout needs significant changes
    <div className="monitoring-alat-container">
      <h2>Jadwal Penggunaan Alat (Disetujui)</h2>

      {error && <div className="error-message">{error}</div>}

      {/* Alat List */}
      {/* Reuse styles from JadwalPenggunaan.css or add specific ones in MonitoringAlat.css */}
      <div className="instrument-list-container">
        <h3>Pilih Alat:</h3>
        {loadingAlat ? (
          <p>Memuat daftar alat...</p>
        ) : (
          <ul className="instrument-list">
            {alatList.map((alat) => (
              <li key={alat.id}>
                <button
                  onClick={() => fetchScheduleForAlat(alat)}
                  className={selectedAlat?.id === alat.id ? 'selected' : ''}
                  disabled={loadingSchedule}
                >
                  {alat.nama} ({alat.type === 'instrumen' ? 'Instrumen' : 'Alat Gelas'})
                </button>
              </li>
            ))}
            {alatList.length === 0 && !loadingAlat && (
              <li>Tidak ada alat yang tersedia.</li>
            )}
          </ul>
        )}
      </div>

      {/* Calendar View */}
      {selectedAlat && (
        <div className="calendar-view-container"> {/* Consistent class name */}
           <h3>Jadwal untuk: {selectedAlat.nama}</h3>
           {loadingSchedule ? (
            <div className="loading-message">Memuat jadwal...</div>
          ) : (
            <Calendar
              localizer={localizer}
              events={scheduleEvents}
              startAccessor="start"
              endAccessor="end"
              style={{ height: 600 }} // Adjust height as needed
              views={['month', 'week', 'day', 'agenda']} // Add agenda view
              step={60}
              showMultiDayTimes
              messages={{
                today: 'Hari Ini',
                previous: 'Sebelumnya',
                next: 'Berikutnya',
                month: 'Bulan',
                week: 'Minggu',
                day: 'Hari',
                agenda: 'Agenda',
                date: 'Tanggal',
                time: 'Waktu',
                event: 'Peminjaman',
                noEventsInRange: 'Tidak ada jadwal penggunaan dalam rentang ini.',
              }}
            />
          )}
           {scheduleEvents.length === 0 && !loadingSchedule && (
             <p style={{ textAlign: 'center', marginTop: '20px' }}>
               Tidak ada jadwal penggunaan yang disetujui untuk alat ini.
             </p>
           )}
        </div>
      )}
    </div>
  );
};

export default MonitoringAlat;
